import React from 'react';

import { ScreenGo, ScreenLink, useScreenState } from '@gogame/screens';

import Actions from 'scenes/Gallery/GalleryPage/Concepts/Actions';
import * as CONCEPT from 'scenes/Gallery/GalleryPage/Concepts/view-constants';
import * as CREATIVE from 'scenes/Gallery/GalleryPage/Creatives/view-constants';
import {
  AttributeList,
  AttributeItem,
} from 'scenes/Gallery/components/AttributeList';
import {
  VIDEO_LENGTH,
  THUMBNAIL_URL,
  SCRUBBING_URLS,
} from 'scenes/Gallery/components/File/constants';
import { Tile } from 'scenes/Gallery/components/Tiles';
import { CONCEPT_ID } from 'scenes/Gallery/GalleryPage/constants';
import { useGalleryPermissions } from 'scenes/Gallery/GalleryPage/hooks';
import { conceptPreviewLink } from 'scenes/Gallery/GalleryPage/navigation';

const ConceptTile = ({ data, isSelected, onSelect, openChildComponent }) => {
  const screenState = useScreenState();

  const { isAllowSharedWith, isAllowExclusiveForAffiliates } =
    useGalleryPermissions();

  const {
    [CONCEPT.ID]: id,
    [CONCEPT.NAME]: name,
    [CONCEPT.APPLICATIONS]: applications,
    [CONCEPT.CREATIVES_COUNT]: creativesCount,
    [CONCEPT.COMMENTS_COUNT]: commentsCount,
    [CONCEPT.IS_NEW]: isNew,
    [CONCEPT.ORIGINAL]: {
      [CREATIVE.CREATIVE_TYPE]: originalCreativeType,
      [CREATIVE.AI_GENERATED]: originalAIGenerated,
      [CREATIVE.FILE]: {
        [THUMBNAIL_URL]: originalFileThumbnailURL,
        [VIDEO_LENGTH]: originalFileVideoLength,
        [SCRUBBING_URLS]: originalFileScrubbingUrls,
      },
    },
    [CONCEPT.EXCLUSIVE_FOR_AFFILIATES]: exclusiveForAffiliates,
    [CONCEPT.IS_PARTIAL_SHARED]: isPartialShared,
    [CONCEPT.IS_SHARED]: isShared,
    [CONCEPT.RATING]: rating,
    [CONCEPT.ACTIONS]: actions,
    [CONCEPT.EXPIRATION_DATE]: expirationDate,
    [CONCEPT.IS_THUMBNAIL_GENERATING]: isThumbnailGenerating,
  } = data;

  const previewData = {
    id,
    name,
    src: originalFileThumbnailURL,
    videoLength: originalFileVideoLength,
    thumbnail: isThumbnailGenerating,
  };

  return (
    <Tile
      selected={isSelected(previewData)}
      onSelect={onSelect(previewData)}
      id={id}
      name={name}
      previewSrc={originalFileThumbnailURL}
      previewThumbnail={isThumbnailGenerating}
      previewScrubbingUrls={originalFileScrubbingUrls}
      renderPreview={({ previewComponent }) => (
        <ScreenGo to={`${id}`} data-cy={'Creative Details'}>
          {previewComponent}
        </ScreenGo>
      )}
      title={
        <ScreenLink
          data-cy={'Concept Page'}
          underline={'always'}
          link={conceptPreviewLink}
          pathParams={{
            [CONCEPT_ID]: id,
          }}
          searchParams={{
            filters: screenState.filters,
            creatives: {
              tableSettings: {
                orderDir: 'asc',
              },
            },
          }}
        >
          {name}
        </ScreenLink>
      }
      content={applications.join(' | ')}
      attributesPreview={
        <AttributeList
          type={'icon'}
          variant={'chips'}
          justifyContent={'end'}
          p={1}
        >
          {isNew ? <AttributeItem valueParser={'new'} /> : null}
          {exclusiveForAffiliates.length && isAllowExclusiveForAffiliates ? (
            <AttributeItem valueParser={'exclusiveForAffiliates'} />
          ) : null}
          {originalAIGenerated ? (
            <AttributeItem valueParser={'aiGenerated'} />
          ) : null}
          {expirationDate ? (
            <AttributeItem
              value={expirationDate}
              valueParser={'expirationDate'}
            />
          ) : null}
        </AttributeList>
      }
      attributes={
        <AttributeList>
          <AttributeItem valueParser={'variantsCount'} value={creativesCount} />
          <AttributeItem
            valueParser={'creativeType'}
            value={originalCreativeType}
          />
          {originalFileVideoLength ? (
            <AttributeItem
              valueParser={'fileVideoLength'}
              value={originalFileVideoLength}
            />
          ) : null}
          {rating ? (
            <AttributeItem valueParser={'rating'} value={rating} />
          ) : null}
          {commentsCount ? (
            <AttributeItem
              valueParser={'commentsCount'}
              value={commentsCount}
            />
          ) : null}
        </AttributeList>
      }
      attributesSecondary={
        <AttributeList type={'icon'}>
          {isPartialShared && isAllowSharedWith ? (
            <AttributeItem
              key={'partialShared'}
              valueParser={'partialShared'}
            />
          ) : isShared && isAllowSharedWith ? (
            <AttributeItem key={'shared'} valueParser={'shared'} />
          ) : null}
        </AttributeList>
      }
      actions={
        <Actions
          actions={actions}
          data={data}
          limit={0}
          onOpenChildComponent={openChildComponent}
          color={'inherit'}
        />
      }
    />
  );
};

export default ConceptTile;
